import { resolveThreshold } from "@/metrics/metric-display"

import {
  AGENT_EVALUATION_SECTION_TITLE,
  DATA_QUALITY_SECTION_TITLE,
  MAX_SECTION_ITEMS,
  MAX_SIGNAL_ITEMS,
  OPERATIONAL_CONTEXT_SECTION_TITLE,
  WHAT_CHANGED_SECTION_TITLE,
  WORTH_DIGGING_SECTION_TITLE,
} from "./runsSummaryConstants"
import {
  formatOperationalMetricList,
  getMetricDisplayName,
  isBinaryMetricForSummary,
  isOperationalContextMetric,
  metricMatchesFocus,
} from "./runsSummaryDetailData"
import { formatCount, formatMetricNumber, pluralize } from "./runsSummaryFormat"
import {
  buildMetricMovementItem,
  compareExactHeadlineMetrics,
  getMetricDirection,
  getMinimumHeadlineMetricCount,
  isPriorityMetric,
  isSignificantMetricMovement,
} from "./runsSummaryMetricDetail"
import type {
  DetailSummary,
  ExactBinaryMetricOutcome,
  MetricWindowStats,
  RunsPageSummaryLookups,
  RunsPageSummarySection,
  RunsSimulationInsights,
  RunsSummaryFocus,
} from "./runsSummaryTypes"

function getExactBinaryMetricOutcome(
  metric: MetricWindowStats,
): ExactBinaryMetricOutcome | null {
  if (
    !metric.isBinaryLike ||
    !metric.metricRecord ||
    !resolveThreshold(metric.metricRecord) ||
    metric.currentAverage === null ||
    metric.currentCount === 0
  ) {
    return null
  }

  const direction = getMetricDirection(
    metric.label,
    metric.metricRecord,
    metric.isBinaryLike,
  )
  if (direction === "neutral") {
    return null
  }

  const rawNegativeShare =
    direction === "positive" ? 1 - metric.currentAverage : metric.currentAverage
  const negativeShare = Math.min(1, Math.max(0, rawNegativeShare))
  const negativeCount = Math.min(
    metric.currentCount,
    Math.max(0, Math.round(negativeShare * metric.currentCount)),
  )

  return { metric, negativeCount, negativeShare }
}

function buildExactBinaryOutcomeItem(
  outcome: ExactBinaryMetricOutcome,
): string {
  if (outcome.negativeCount === 0) {
    return `${outcome.metric.label} met its configured target on all ${formatCount(
      outcome.metric.currentCount,
    )} evaluated simulations.`
  }

  return `${outcome.metric.label} missed its configured target on ${formatCount(
    outcome.negativeCount,
  )} of ${formatCount(outcome.metric.currentCount)} evaluated simulations.`
}

export function buildExactSimulationDetailSummary(
  insights: RunsSimulationInsights,
  lookups: RunsPageSummaryLookups,
  focus: RunsSummaryFocus,
): DetailSummary {
  const formatExactMetricAverage = (
    metric: MetricWindowStats,
    value: number,
  ): string => {
    if (!metric.isBinaryLike) {
      return formatMetricNumber(value)
    }
    return `${new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 1,
    }).format(value * 100)}%`
  }
  const total = insights.current.totalSimulations
  const scorable = Math.max(0, total - insights.current.unscorableSimulations)
  const subject = "the selected simulations"
  if (total === 0) {
    const noDataItem =
      "No simulations matched the current filters in this window."
    return {
      currentRows: [],
      detailRowCount: 0,
      leadSentence: noDataItem,
      sections: [],
      signalItems: [noDataItem],
    }
  }

  const metricStats: MetricWindowStats[] = insights.metrics.map((metric) => {
    const metricRecord = lookups.metricRecordsById?.[metric.metricId]
    return {
      id: metric.metricId,
      label: getMetricDisplayName(metric.metricId, lookups),
      currentAverage: metric.currentAverage,
      previousAverage: metric.previousAverage,
      currentCount: metric.currentCount,
      previousCount: metric.previousCount,
      currentDangerCount: 0,
      // The aggregate flags any float metric whose observed values happen to
      // be only 0/1 as binary. Confirm the metric's semantic type before
      // turning an average into exact pass/fail counts (for example, Music
      // Detection is a numerical segment count that is often 0 or 1).
      isBinaryLike: isBinaryMetricForSummary(metric.isBinary, metricRecord),
      metricRecord,
    }
  })
  const evaluationMetricStats = metricStats.filter(
    (metric) => !isOperationalContextMetric(metric),
  )
  const operationalMetricStats = metricStats.filter(isOperationalContextMetric)
  const minimumHeadlineMetricCount = getMinimumHeadlineMetricCount(scorable)
  const headlineMetrics = evaluationMetricStats
    .filter(
      (metric) =>
        metric.currentCount > 0 &&
        metric.currentCount >= minimumHeadlineMetricCount &&
        metric.currentAverage !== null &&
        getMetricDirection(
          metric.label,
          metric.metricRecord,
          metric.isBinaryLike,
        ) !== "neutral",
    )
    .sort((a, b) => compareExactHeadlineMetrics(a, b, focus))
  const primaryMetric = headlineMetrics[0]
  evaluationMetricStats.sort((a, b) => {
    const primaryDelta =
      Number(b.id === primaryMetric?.id) - Number(a.id === primaryMetric?.id)
    const focusDelta =
      Number(metricMatchesFocus(b.id, b.label, focus)) -
      Number(metricMatchesFocus(a.id, a.label, focus))
    const priorityDelta =
      Number(isPriorityMetric(b.label)) - Number(isPriorityMetric(a.label))
    return (
      primaryDelta ||
      focusDelta ||
      priorityDelta ||
      b.currentCount - a.currentCount
    )
  })
  operationalMetricStats.sort(
    (a, b) => b.currentCount - a.currentCount || a.label.localeCompare(b.label),
  )

  const buildExactMetricItems = (metrics: MetricWindowStats[]): string[] =>
    metrics
      .filter(
        (metric) => metric.currentCount > 0 && metric.currentAverage !== null,
      )
      .map((metric) => {
        if (metric.previousCount > 0 && metric.previousAverage !== null) {
          return buildMetricMovementItem(
            metric,
            { isLikeForLike: true, changedDimensions: [] },
            false,
            formatExactMetricAverage,
          )
        }
        return `${metric.label} averaged ${formatExactMetricAverage(
          metric,
          metric.currentAverage as number,
        )} across ${formatCount(metric.currentCount)} measured ${pluralize(
          metric.currentCount,
          "simulation",
        )}; there is no prior-window baseline.`
      })
      .filter((item): item is string => Boolean(item))
      .slice(0, MAX_SECTION_ITEMS)
  const operationalMetricItems = buildExactMetricItems(operationalMetricStats)
  const evaluationMovementItems = buildExactMetricItems(
    evaluationMetricStats.filter(isSignificantMetricMovement),
  )
  const exactBinaryOutcomes = evaluationMetricStats
    .map(getExactBinaryMetricOutcome)
    .filter((outcome): outcome is ExactBinaryMetricOutcome => outcome !== null)
    .sort((a, b) => {
      const focusDelta =
        Number(metricMatchesFocus(b.metric.id, b.metric.label, focus)) -
        Number(metricMatchesFocus(a.metric.id, a.metric.label, focus))
      return (
        focusDelta ||
        b.negativeShare - a.negativeShare ||
        b.negativeCount - a.negativeCount ||
        b.metric.currentCount - a.metric.currentCount
      )
    })
  const concerningOutcomeItems = exactBinaryOutcomes
    .filter((outcome) => outcome.negativeCount > 0)
    .map(buildExactBinaryOutcomeItem)
    .slice(0, MAX_SECTION_ITEMS)
  const successfulOutcomeItems = exactBinaryOutcomes
    .filter((outcome) => outcome.negativeCount === 0)
    .map(buildExactBinaryOutcomeItem)
    .slice(0, MAX_SECTION_ITEMS)
  const worseMovementItems = evaluationMovementItems.filter((item) =>
    item.includes("(worse)"),
  )
  const diggingItems = [...concerningOutcomeItems, ...worseMovementItems]
    .filter((item, index, items) => items.indexOf(item) === index)
    .slice(0, MAX_SECTION_ITEMS)

  const personaItems = primaryMetric
    ? insights.personaMetrics
        .filter(
          (metric) =>
            metric.metricId === primaryMetric.id &&
            metric.currentCount > 0 &&
            metric.currentAverage !== null,
        )
        .sort((a, b) => b.currentCount - a.currentCount)
        .slice(0, MAX_SECTION_ITEMS)
        .map((metric) => {
          const persona =
            lookups.personaNames?.[metric.personaId] ?? metric.personaId
          const previousClause =
            metric.previousCount > 0 && metric.previousAverage !== null
              ? `, vs ${formatExactMetricAverage(
                  primaryMetric,
                  metric.previousAverage,
                )} previously`
              : ""
          return `${persona}: ${formatExactMetricAverage(
            primaryMetric,
            metric.currentAverage as number,
          )} across ${formatCount(metric.currentCount)} measured ${pluralize(
            metric.currentCount,
            "simulation",
          )}${previousClause}.`
        })
    : []

  const dataQualityItems: string[] = []
  if (insights.current.unscorableSimulations > 0) {
    const unscorable = insights.current.unscorableSimulations
    const failed = insights.current.failedSimulations
    const unscorablePopulation = `${formatCount(unscorable)} of ${formatCount(
      total,
    )} ${pluralize(total, "simulation")}`
    if (failed === unscorable) {
      dataQualityItems.push(
        `${unscorablePopulation} produced no metric output because ${pluralize(
          failed,
          "it failed",
          "they failed",
        )} at execution time; behavioral rates exclude ${pluralize(
          failed,
          "it",
          "them",
        )}.`,
      )
    } else if (failed > 0 && failed < unscorable) {
      dataQualityItems.push(
        `${unscorablePopulation} produced no metric output, including ${formatCount(
          failed,
        )} that failed at execution time; behavioral rates exclude all ${formatCount(
          unscorable,
        )}.`,
      )
    } else {
      dataQualityItems.push(
        `${unscorablePopulation} produced no metric output; behavioral rates exclude ${pluralize(
          unscorable,
          "it",
          "them",
        )}.`,
      )
    }
  }
  if (
    insights.current.failedSimulations > insights.current.unscorableSimulations
  ) {
    dataQualityItems.push(
      `${formatCount(insights.current.failedSimulations)} ${pluralize(
        insights.current.failedSimulations,
        "simulation failed",
        "simulations failed",
      )} at execution time; this is separate from behavioral metric failure.`,
    )
  }

  const sections: RunsPageSummarySection[] = []
  if (evaluationMovementItems.length > 0) {
    sections.push({
      title: WHAT_CHANGED_SECTION_TITLE,
      items: evaluationMovementItems,
    })
  }
  if (diggingItems.length > 0) {
    sections.push({
      title: WORTH_DIGGING_SECTION_TITLE,
      items: diggingItems,
    })
  }
  if (successfulOutcomeItems.length > 0) {
    sections.push({
      title: AGENT_EVALUATION_SECTION_TITLE,
      items: successfulOutcomeItems,
    })
  }
  if (personaItems.length > 1) {
    sections.push({ title: "By Persona", items: personaItems })
  }
  if (operationalMetricItems.length > 0) {
    sections.push({
      title: OPERATIONAL_CONTEXT_SECTION_TITLE,
      items: operationalMetricItems,
    })
  }
  if (dataQualityItems.length > 0) {
    sections.push({
      title: DATA_QUALITY_SECTION_TITLE,
      items: dataQualityItems,
    })
  }

  const evaluatedSimulationCount = evaluationMetricStats.reduce(
    (highestCount, metric) => Math.max(highestCount, metric.currentCount),
    0,
  )
  const operationalMetricList = formatOperationalMetricList(
    operationalMetricStats,
  )
  const failedSimulationsDominate =
    insights.current.failedSimulations * 2 > total
  const unscorableSimulationsDominate =
    insights.current.unscorableSimulations * 2 > total
  const scoredEvaluationMetricCount = evaluationMetricStats.filter(
    (metric) => metric.currentCount > 0 && metric.currentAverage !== null,
  ).length
  const primaryConcernItem = concerningOutcomeItems[0]
  const primaryWorseMovementItem = worseMovementItems[0]
  const primarySuccessfulItem = successfulOutcomeItems[0]
  const successfulLead = primarySuccessfulItem
    ? scoredEvaluationMetricCount === 1
      ? `${primarySuccessfulItem.replace(/\.$/, "")}; it was the only agent-evaluation metric scored, so this window does not assess broader agent behavior.`
      : primarySuccessfulItem
    : null
  const leadSentence = failedSimulationsDominate
    ? `${formatCount(insights.current.failedSimulations)} of ${formatCount(
        total,
      )} simulations failed at execution time, so the recent runs do not contain enough behavioral evidence to evaluate.`
    : scorable === 0
      ? `No behavioral conclusion is available for ${subject}: ${formatCount(
          total,
        )} ${pluralize(total, "simulation")} produced no metric outputs.`
      : unscorableSimulationsDominate
        ? `Only ${formatCount(scorable)} of ${formatCount(
            total,
          )} simulations produced metric output, so behavioral findings are too sparse to headline reliably.`
        : primaryConcernItem
          ? primaryConcernItem
          : primaryWorseMovementItem
            ? primaryWorseMovementItem
            : successfulLead
              ? successfulLead
              : evaluationMetricStats.some(
                    (metric) =>
                      metric.currentCount > 0 && metric.currentAverage !== null,
                  )
                ? `Agent-evaluation output is available for ${formatCount(
                    evaluatedSimulationCount,
                  )} of ${formatCount(
                    total,
                  )} simulations, but no configured target identifies a clear pass or failure direction.`
                : operationalMetricList
                  ? `No agent-evaluation metrics were scored for ${subject}; only operational context is available for ${operationalMetricList}.`
                  : `${formatCount(scorable)} of ${formatCount(total)} ${pluralize(
                      total,
                      "simulation has",
                      "simulations have",
                    )} metric output, but no numeric metric aggregate is available.`

  return {
    currentRows: [],
    detailRowCount: total,
    leadSentence,
    sections,
    signalItems: [
      ...concerningOutcomeItems,
      ...evaluationMovementItems,
      ...successfulOutcomeItems,
      ...personaItems,
      ...dataQualityItems,
      ...operationalMetricItems,
    ].slice(0, MAX_SIGNAL_ITEMS),
  }
}
